import React from 'react';
import { Badge } from '@syneto/compass-react';
import { FIRMWARE_COMPARISON, NOT_AVAILABLE } from '../../../../constants/hardware-inventory';

const DetailRow = (props) => {
	const { label, children } = props;

	return (
		<li className="pt-2 pb-1 top-border">
			<label className="pf-3">{label}: </label>
			<span className="text-value">{children}</span>
		</li>
	);
};

const valueOf = (value) => {
	return value ?? NOT_AVAILABLE;
};

/*
 * The raw manufacturer/model are what the appliance reported; the plain ones are the normalized
 * catalog values the firmware analytics are keyed on. Both are shown because a mismatch between
 * them is exactly what a normalization bug looks like from the outside.
 */
const renderReported = (raw, normalized) => {
	if (!raw) {
		return valueOf(normalized);
	}

	if (!normalized || raw.toLowerCase() === normalized.toLowerCase()) {
		return raw;
	}

	return `${raw} (${normalized})`;
};

const FleetComponentDetails = (props) => {
	const { component, componentType } = props;

	/*
	 * "Oldest" only says something when the fleet spans more than one version. On a type where every
	 * appliance reports the same firmware, every component is at once the oldest and the newest, and
	 * the badge would fire on all of them while distinguishing none.
	 *
	 * This is the same equality the fleet-wide "Fragmented" status was built on, kept here because
	 * the question it answers is different. As a verdict on a component type it was true of nearly
	 * every row and so carried no information; as a guard on a per-component badge it earns its place
	 * by suppressing one. Equality only - the API owns version ordering (`firmwareComparison`).
	 */
	const isFleetSplit = Boolean(componentType?.oldestFirmwareVersion)
		&& Boolean(componentType?.newestFirmwareVersion)
		&& componentType.oldestFirmwareVersion !== componentType.newestFirmwareVersion;

	const isOldestInFleet = Boolean(component.firmwareVersion)
		&& component.firmwareVersion === componentType?.oldestFirmwareVersion
		&& isFleetSplit;

	/*
	 * The verdict is the API's, not ours. Equality against `approvedFirmwareVersion` only ever yielded
	 * "same" or "not the same", which reported an appliance running *newer* firmware as behind and
	 * told the engineer to flash down. Ranking the two here instead is not an option: the ordering
	 * is version-aware, so `10.0` against `9.0` needs the comparison the API already does.
	 */
	const isBehindApproved = component.firmwareComparison === FIRMWARE_COMPARISON.BEHIND;
	const isAheadOfApproved = component.firmwareComparison === FIRMWARE_COMPARISON.AHEAD;
	const isOnApprovedVersion = component.firmwareComparison === FIRMWARE_COMPARISON.MATCH;

	return (
		<div>
			<div className="row">
				<div className="col-6">
					<ul className="list-unstyled">
						<li className="pt-2 pb-1"><h5>Component</h5></li>
						<DetailRow label="Type">{valueOf(component.displayName)}</DetailRow>
						<DetailRow label="Category">{valueOf(component.category)}</DetailRow>
						<DetailRow label="Manufacturer">
							{renderReported(component.manufacturerRaw, component.manufacturer)}
						</DetailRow>
						<DetailRow label="Model">
							{renderReported(component.modelRaw, component.model)}
						</DetailRow>
						<DetailRow label="Firmware version">{valueOf(component.firmwareVersion)}</DetailRow>
						<DetailRow label="Location">{valueOf(component.location)}</DetailRow>
					</ul>
				</div>

				<div className="col-6">
					<ul className="list-unstyled">
						<li className="pt-2 pb-1"><h5>Appliance</h5></li>
						<DetailRow label="Serial number">{valueOf(component.applianceSerialNumber)}</DetailRow>
					</ul>
				</div>
			</div>

			{/* Rendered only when the (category, manufacturer, model) join finds an analytics row. A miss
				is expected rather than an error: the analytics table is an inner join, so a component
				type has no row between its creation and the next refresh covering it. */}
			{componentType && (
				<div className="row">
					<div className="col-12">
						<ul className="list-unstyled">
							<li className="pt-2 pb-1"><h5>Fleet firmware for this component type</h5></li>
							<DetailRow label="Oldest in fleet">{valueOf(componentType.oldestFirmwareVersion)}</DetailRow>
							<DetailRow label="Newest in fleet">{valueOf(componentType.newestFirmwareVersion)}</DetailRow>
						</ul>

						{/* No "Approved" row on purpose, and it must not come back. The badge below is ranked by
							the API against the *live* approved version, while `componentType` carries the
							materialized copy from the analytics table - which lags an approval, and keeps lagging
							until the next refresh covers the type. Printing the stale number beside a verdict
							computed from the fresh one reads as a contradiction: "Approved: D11" above
							"Behind the approved version" for a component on D12. The verdict is the accurate half,
							so the number is what goes. The Firmware by Type tab still lists the approved version -
							with its approver and date - where nothing disputes it. */}

						{/* All three verdicts draw a badge, including `match`. Only an *absent* verdict draws
							nothing. Leaving match silent made a bare row ambiguous between "ranked it, and this
							component is on the approved version" and "could not rank it at all" - and until
							SYN-2393 records an approval, every row is the second. Saying it explicitly is what
							separates a component that passed from a check that never ran. The three are mutually
							exclusive, so at most one renders and only the fleet badge before them needs spacing. */}
						{(isOldestInFleet || isBehindApproved || isAheadOfApproved || isOnApprovedVersion) && (
							<div className="pt-2">
								{isOldestInFleet && (
									<Badge appearance="warning" className="me-2">Oldest firmware in the fleet</Badge>
								)}
								{isBehindApproved && (
									<Badge appearance="warning">Behind the approved version</Badge>
								)}
								{isAheadOfApproved && (
									<Badge appearance="info">Ahead of the approved version</Badge>
								)}
								{isOnApprovedVersion && (
									<Badge appearance="success">On the approved version</Badge>
								)}
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default FleetComponentDetails;
